import { useEffect, useState } from "react";
import { NavLink, Route, Routes, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Target, ChartLineUp, Users, Link as LinkIcon, CurrencyDollar,
  ShieldWarning, ClockCounterClockwise, SignOut, DownloadSimple, Gear, DiscordLogo,
} from "@phosphor-icons/react";
import api, { logout, API } from "@/lib/api";
import { OWNER } from "@/constants/testIds";
import OverviewTab from "@/components/owner/OverviewTab";
import CreatorsTab from "@/components/owner/CreatorsTab";
import CreatorDetail from "@/pages/CreatorDetail";
import LinksTab from "@/components/owner/LinksTab";
import MembersTab from "@/components/owner/MembersTab";
import CustomersTab from "@/components/owner/CustomersTab";
import PurchasesTab from "@/components/owner/PurchasesTab";
import SalesTab from "@/components/owner/SalesTab";
import AuditTab from "@/components/owner/AuditTab";
import FraudTab from "@/components/owner/FraudTab";
import SettingsTab from "@/components/owner/SettingsTab";

function NavItem({ to, icon: Icon, label, testid, end }) {
  return (
    <NavLink to={to} end={end} data-testid={testid} className={({ isActive }) =>
      `flex items-center gap-3 px-4 py-3 border-l-2 transition-colors duration-150 ${isActive ? "border-neon-cyan bg-white/5 text-white" : "border-transparent text-white/60 hover:text-white hover:bg-white/[0.03]"}`}>
      <Icon size={18} weight="duotone" />
      <span className="kbd-label">{label}</span>
    </NavLink>
  );
}

export default function OwnerDashboard() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);

  useEffect(() => {
    api.get("/auth/owner/me").then(r => setMe(r.data)).catch(() => { logout(); nav("/owner/login"); });
  }, [nav]);

  function doLogout() { logout(); toast.success("LOGGED OUT"); nav("/"); }

  function downloadCsv(path, testid) {
    const token = localStorage.getItem("gtarp_token");
    fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob()).then(b => {
        const url = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = url; a.download = path.split("/").pop() + ".csv"; a.click();
        URL.revokeObjectURL(url);
      });
  }

  return (
    <div className="min-h-screen grid" style={{ gridTemplateColumns: "240px 1fr" }}>
      {/* Sidebar */}
      <aside className="bg-obsidian-surface border-r border-white/10 sticky top-0 h-screen flex flex-col">
        <div className="p-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-neon-cyan flex items-center justify-center">
              <Target size={18} weight="bold" color="#050505" />
            </div>
            <div>
              <div className="kbd-label text-neon-cyan">CONSOLE</div>
              <div className="display-heading font-black text-sm leading-tight">LIQUID RP<span className="text-neon-cyan"> CREATORS</span></div>
            </div>
          </div>
        </div>

        <nav className="flex-1 py-3">
          <NavItem to="/owner" end icon={ChartLineUp} label="OVERVIEW" testid={OWNER.navOverview} />
          <NavItem to="/owner/creators" icon={Users} label="CREATORS" testid={OWNER.navCreators} />
          <NavItem to="/owner/members" icon={DiscordLogo} label="MEMBERS" testid="nav-members" />
          <NavItem to="/owner/customers" icon={Users} label="CUSTOMERS" testid="nav-customers" />
          <NavItem to="/owner/links" icon={LinkIcon} label="LINKS" />
          <NavItem to="/owner/sales" icon={CurrencyDollar} label="SALES CSV" testid="nav-sales" />
          <NavItem to="/owner/purchases" icon={CurrencyDollar} label="PURCHASES" testid={OWNER.navPurchases} />
          <NavItem to="/owner/fraud" icon={ShieldWarning} label="FRAUD FLAGS" testid={OWNER.navFraud} />
          <NavItem to="/owner/audit" icon={ClockCounterClockwise} label="AUDIT LOG" testid={OWNER.navAudit} />
          <NavItem to="/owner/settings" icon={Gear} label="SETTINGS" testid="nav-settings" />
        </nav>

        <div className="p-4 border-t border-white/10 space-y-3">
          <div>
            <div className="kbd-label text-white/40">SIGNED IN</div>
            <div className="font-mono text-xs truncate">{me?.email}</div>
          </div>
          <button onClick={doLogout} data-testid={OWNER.logout} className="ghost-btn w-full py-2 kbd-label inline-flex items-center justify-center gap-2 text-white">
            <SignOut size={14} /> LOGOUT
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="min-w-0">
        <header className="border-b border-white/10 px-8 py-6 flex items-center justify-between bg-obsidian sticky top-0 z-10">
          <div>
            <div className="kbd-label text-neon-cyan">// OWNER CONSOLE</div>
            <h1 className="display-heading font-black text-2xl">Command Center</h1>
          </div>
          <div className="flex gap-2">
            <button onClick={() => downloadCsv("/exports/creators")} data-testid={OWNER.exportCreators} className="ghost-btn px-4 py-2 kbd-label inline-flex items-center gap-2 text-white">
              <DownloadSimple size={14} /> CREATORS CSV
            </button>
            <button onClick={() => downloadCsv("/exports/purchases")} data-testid={OWNER.exportPurchases} className="ghost-btn px-4 py-2 kbd-label inline-flex items-center gap-2 text-white">
              <DownloadSimple size={14} /> PURCHASES CSV
            </button>
          </div>
        </header>
        <div className="p-8">
          <Routes>
            <Route index element={<OverviewTab />} />
            <Route path="creators" element={<CreatorsTab />} />
            <Route path="creators/:id" element={<CreatorDetail />} />
            <Route path="members" element={<MembersTab />} />
            <Route path="customers" element={<CustomersTab />} />
            <Route path="links" element={<LinksTab />} />
            <Route path="sales" element={<SalesTab />} />
            <Route path="purchases" element={<PurchasesTab />} />
            <Route path="fraud" element={<FraudTab />} />
            <Route path="audit" element={<AuditTab />} />
            <Route path="settings" element={<SettingsTab />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}
